import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Mail, MapPin } from "lucide-react";
import { SITE } from "@/content/site";
import logo from "@/assets/logo.webp";
import { LOCATIONS } from "@/content/locations";
import { RANGPUR_AREAS } from "@/content/rangpur-areas";
import { NewsletterGate } from "@/components/NewsletterGate";

const COLS = [
  {
    title: "Company",
    links: [
      { to: "/about", label: "About" },
      { to: "/process", label: "Process" },
      { to: "/case-studies", label: "Case Studies" },
      { to: "/testimonials", label: "Testimonials" },
      { to: "/pricing", label: "Pricing" },
      { to: "/book", label: "Book a Call" },
      { to: "/contact", label: "Contact" },
    ],
  },
  {
    title: "Resources",
    links: [
      { to: "/blog", label: "Blog" },
      { to: "/resources", label: "Free Resources" },
      { to: "/aeo", label: "AEO Hub" },
      { to: "/bangladesh-seo", label: "Bangladesh SEO Hub" },
      { to: "/glossary", label: "Glossary" },
      { to: "/ai-answers", label: "AI Answers" },
      { to: "/free-seo-audit", label: "Free Audit Tool" },
      { to: "/compare", label: "Compare Providers" },
      { to: "/sitemap", label: "Sitemap" },
    ],
  },
  {
    title: "Free Tools",
    links: [
      { to: "/tools", label: "All Tools" },
      { to: "/tools/serp-simulator", label: "SERP Simulator" },
      { to: "/tools/schema-generator", label: "Schema Generator" },
      { to: "/tools/robots-tester", label: "Robots.txt Tester" },
      { to: "/aeo-checklist", label: "AEO Checklist (PDF)" },
    ],
  },
] as const;

export function Footer() {
  return (
    <footer className="cv-section relative mt-32 border-t border-border">
      <div className="pointer-events-none absolute inset-0 opacity-50" style={{ background: "var(--gradient-radial)" }} />
      <div className="relative container-x py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <img src={logo} alt="Freelancer Tamal" width={720} height={268} loading="lazy" decoding="async" className="h-24 md:h-28 lg:h-32 w-auto mb-6" />
            <h3 className="font-display text-3xl md:text-4xl leading-tight">
              Let's make your brand <br />
              <span className="text-gradient">impossible to ignore.</span>
            </h3>
            <p className="mt-4 max-w-md text-sm text-muted-foreground">
              Premium SEO, AEO and GEO services led by Tamal — helping ambitious
              brands in Bangladesh and beyond win in Google and AI engines.
            </p>
            <Link
              to="/contact"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-3 text-sm font-medium hover:shadow-[var(--shadow-glow)] transition"
            >
              Start a project <ArrowUpRight className="h-4 w-4" />
            </Link>

            <div className="mt-8 space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" /> {SITE.city}, {SITE.country}
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-primary" />
                <a href={`mailto:${SITE.email}`} className="hover:text-foreground">{SITE.email}</a>
              </div>
            </div>

            <div className="mt-10 rounded-2xl border border-border bg-card/60 p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Newsletter</div>
              <div className="font-display text-lg leading-snug mb-3">
                Get the AEO checklist + monthly SEO playbook.
              </div>
              <NewsletterGate
                fileUrl="/aeo-implementation-checklist.pdf"
                promise="Send it"
                source="footer"
                compact
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-5 lg:col-span-7">
            {COLS.map((col) => (
              <div key={col.title}>
                <div className="mb-4 text-xs uppercase tracking-[0.2em] text-primary/80">
                  {col.title}
                </div>
                <ul className="space-y-2.5">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      <Link
                        to={l.to}
                        className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                  {col.title === "Resources" && (
                    <li>
                      <a
                        href="https://tool.freelancertamal.com/"
                        target="_blank"
                        rel="noopener"
                        className="text-sm text-primary transition-colors hover:text-foreground"
                      >
                        Free Audit Tool (Live) ↗
                      </a>
                    </li>
                  )}
                </ul>
              </div>
            ))}
            <div>
              <div className="mb-4 text-xs uppercase tracking-[0.2em] text-primary/80">Locations</div>
              <ul className="space-y-2.5">
                {LOCATIONS.slice(0, 5).map((l) => (
                  <li key={l.slug}>
                    <Link
                      to="/seo-services/$location"
                      params={{ location: l.slug }}
                      className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      SEO in {l.city}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/seo-locations" className="text-sm text-primary hover:text-foreground">All locations →</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 border-t border-border pt-8">
          <div className="mb-4 text-xs uppercase tracking-[0.2em] text-primary/80">Rangpur — area pages</div>
          <ul className="flex flex-wrap gap-x-4 gap-y-2">
            {RANGPUR_AREAS.map((a) => (
              <li key={a.slug}>
                <Link
                  to="/seo-services/rangpur/$area"
                  params={{ area: a.slug }}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  {a.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-border pt-6 text-xs text-muted-foreground sm:flex-row sm:items-center">
          <div>© {new Date().getFullYear()} {SITE.name}. All rights reserved.</div>
          <div className="flex gap-5">
            <Link to="/credentials" className="hover:text-foreground">Credentials</Link>
            <Link to="/proof" className="hover:text-foreground">Proof</Link>
            <Link to="/press" className="hover:text-foreground">Press</Link>
            <Link to="/trust" className="hover:text-foreground">Trust</Link>
            <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
          </div>
          <div className="flex gap-5">
            <a href={SITE.social.linkedin} className="hover:text-foreground">LinkedIn</a>
            <a href={SITE.social.facebook} className="hover:text-foreground">Facebook</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
